import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, Check, Loader2, MapPin } from 'lucide-react'
import type { CreatedProduct } from '@/types/products'
import type { ApiStorageNode, StorageCondition, StorageConditionMeta } from '@/types/storage'
import { useCatalogEntriesByIds } from '@/hooks/useProducts'
import {
  useCapacitySummary,
  useFreeSlots,
  useRefreshStorageAvailability,
  useSetProductLocation,
  useStorageConditions,
} from '@/hooks/useStorage'
import {
  filterUnitsByCondition,
  pickFreePosition,
  pickUnit,
  resolveRequirement,
  type StorageRequirement,
} from '@/lib/productStorage'
import { LocationTrail } from '@/components/inventory/storage/picker/LocationTrail'
import { StorageLocationPicker } from '@/components/inventory/storage/picker/StorageLocationPicker'
import { profileLabel } from '@/lib/storageTempMapping'
import { usePermission } from '@/lib/permissions'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

/** A unit's condition as the picker names it. Shared with the batch dialog. */
export function unitConditionLabel(
  condition: StorageCondition | null,
  conditions: StorageConditionMeta[],
): string {
  return condition ? profileLabel(condition, conditions) : 'No condition declared'
}

/**
 * The storage requirement of the products being placed, read from their own
 * catalog entries rather than handed in: a caller's copy may be missing (the
 * product came from another submission) or not loaded yet, and either would
 * have read as "any unit allowed" and ended in a 409 at Confirm.
 *
 * `null` from the query means "no product", which leaves it pending; the
 * dialog is closed then anyway.
 */
export function useStorageRequirement(catalogEntryIds: string[]): StorageRequirement {
  const entries = useCatalogEntriesByIds(catalogEntryIds)
  if (entries.isError) {
    return {
      status: 'error',
      message: entries.error instanceof Error ? entries.error.message : 'unknown error',
    }
  }
  return resolveRequirement(catalogEntryIds, entries.byId, entries.isPending)
}

/** The requirement, once known. Null while it is not — nothing may be offered then. */
export function requiredConditionOf(requirement: StorageRequirement): StorageCondition | null {
  return requirement.status === 'ready' ? requirement.requiredCondition : null
}

/**
 * What the picker is narrowed to, above the units: the mapped profile and how
 * many units it hid, or — for an unmapped temperature — that nothing was
 * checked. Shared with the batch dialog so both say it the same way.
 *
 * Says "unmapped" only once the catalog entry has loaded.
 */
export function RequirementNotice({
  requirement,
  hidden,
  conditions,
}: {
  requirement: StorageRequirement
  hidden: number
  conditions: StorageConditionMeta[]
}) {
  const { isSuperAdmin } = usePermission()
  if (requirement.status === 'loading') {
    return (
      <p className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Checking storage requirement…
      </p>
    )
  }
  if (requirement.status === 'error') {
    return (
      <p className='flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive'>
        <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
        Could not read this product's storage requirement: {requirement.message}
      </p>
    )
  }
  if (requirement.status === 'conflict') {
    return (
      <p className='flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive'>
        <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
        These products need different storage conditions — place them one at a time.
      </p>
    )
  }
  const { requiredCondition, storageTemp } = requirement
  if (requiredCondition) {
    return (
      <p className='text-sm'>
        <span className='font-medium'>Requires {profileLabel(requiredCondition, conditions)}</span>
        {hidden > 0 && (
          <span className='text-muted-foreground'>
            {' '}
            · {hidden} unit{hidden === 1 ? '' : 's'} at other conditions hidden
          </span>
        )}
      </p>
    )
  }
  const raw = storageTemp?.trim()
  return (
    <p className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
      <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
      <span>
        {raw
          ? `Unmapped storage temperature '${raw}' — any unit allowed.`
          : 'No storage temperature on this product code — any unit allowed.'}
        {raw && isSuperAdmin && (
          <>
            {' '}
            <Link to='/configure?tab=products' className='font-medium underline'>
              Map it in Configure → Products
            </Link>
          </>
        )}
      </span>
    </p>
  )
}


/**
 * The prompt after Print Label (Create New Product spec): the system proposes
 * a location from the product's storage profile; the operator confirms it or
 * picks another via Choose another location — which also offers Temp
 * Locations — and the chosen slot is reserved (Status = Reserved).
 *
 * Units, and the positions inside them, are narrowed to the condition the
 * product's catalog temperature is mapped to (Configure → Products → Storage
 * Temp Mapping), the same rule the server applies on put-away. An unmapped
 * temperature allows any unit.
 *
 * Every location is shown as its full address, Facility › Building › Floor ›
 * Room › unit › … › slot, and choosing one walks the same way (z8nrz7fqfy).
 * Slots come from the free-slots endpoint, so a unit whose slots are declared
 * is offered rather than read as full, and opening a large freezer costs one
 * page of slots rather than its whole subtree.
 */
export function StorageAssignmentDialog({
  product,
  onClose,
}: {
  product: CreatedProduct | null
  onClose: () => void
}) {
  const [browsing, setBrowsing] = useState(false)
  /** The slot chosen in the picker, before it is reserved. */
  const [chosen, setChosen] = useState<ApiStorageNode | null>(null)
  /** The slot the server accepted — persisted, not a client-side note. */
  const [assignedPath, setAssignedPath] = useState<string | null>(null)

  const setProductLocation = useSetProductLocation()
  const refreshAvailability = useRefreshStorageAvailability()
  // Slot availability is volatile, so every open asks again. What is already
  // held shows meanwhile; the server refuses a slot taken since (409).
  useEffect(() => {
    if (product) refreshAvailability()
  }, [product, refreshAvailability])

  const requirement = useStorageRequirement(product ? [product.catalogEntryId] : [])
  const requires = requiredConditionOf(requirement)
  // Nothing is offered until the requirement is known.
  const blocked = requirement.status !== 'ready'
  const storageTemp = requirement.status === 'ready' ? requirement.storageTemp : undefined
  // Only while open: the dialog is mounted on every procedure page (z8nrz7e8kw).
  const summary = useCapacitySummary(!!product)
  const conditions = useStorageConditions().data?.conditions ?? []
  const byCondition = useMemo(
    () =>
      blocked
        ? { eligible: [], hidden: 0 }
        : filterUnitsByCondition(summary.data ?? [], requires),
    [summary.data, requires, blocked],
  )
  const picked = useMemo(
    () => pickUnit(byCondition.eligible, requires),
    [byCondition.eligible, requires],
  )
  const unitsWithRoom = useMemo(() => byCondition.eligible.filter(u => u.remaining > 0), [byCondition.eligible])

  // The suggestion is the first free slot of the suggested unit: one slot, so
  // one small read whatever the unit's size.
  const suggestion = useFreeSlots(product && !blocked ? picked?.unit.nodeId : null, {
    condition: requires ?? undefined,
    limit: 1,
  })
  const suggested = useMemo(
    () => (suggestion.data ? pickFreePosition(suggestion.data.nodes, requires) : null),
    [suggestion.data, requires],
  )

  function reset() {
    setBrowsing(false)
    setChosen(null)
    setAssignedPath(null)
    setProductLocation.reset()
  }

  function handleReserve(slot: ApiStorageNode) {
    if (!product) return
    setProductLocation.mutate(
      { productId: product.id, storageNodeId: slot.id },
      { onSuccess: () => setAssignedPath(slot.path) },
    )
  }

  function handleClose() {
    reset()
    onClose()
  }

  // A cached "no units" answer must never decide where a product goes: with
  // no room on record, wait for the fresh answer before saying so.
  const loading =
    summary.isLoading ||
    (summary.isFetching && unitsWithRoom.length === 0) ||
    (!browsing && picked != null && suggestion.isLoading)
  const target = browsing ? chosen : suggested

  return (
    <Dialog open={!!product} onOpenChange={o => !o && handleClose()}>
      <DialogContent className={cn('transition-[max-width]', browsing ? 'sm:max-w-3xl' : 'sm:max-w-xl')}>
        <DialogHeader>
          <DialogTitle>Storage location</DialogTitle>
          <DialogDescription>
            {product ? `DIN ${product.din}` : ''} — designated location based on the storage profile
            {storageTemp ? ` (${storageTemp})` : ''}.
          </DialogDescription>
        </DialogHeader>

        {!assignedPath && (
          <RequirementNotice requirement={requirement} hidden={byCondition.hidden} conditions={conditions} />
        )}

        {assignedPath ? (
          <div className='space-y-3 rounded-xl border border-green-200 bg-green-50 p-4'>
            <p className='flex items-center gap-2 text-sm font-semibold text-green-800'>
              <Check className='h-4 w-4' />
              Location assigned
            </p>
            <LocationTrail path={assignedPath} className='rounded-lg bg-white px-3 py-2' />
            <p className='text-xs text-green-800'>
              Saved — the slot now shows this product and reads as occupied on the storage map.
            </p>
          </div>
        ) : blocked ? null : loading ? (
          <div className='flex items-center justify-center py-10'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : summary.isError || suggestion.isError ? (
          // An error is not "no free space" — say which read failed so a stale
          // backend or auth problem cannot pass for a full freezer.
          <div className='flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive'>
            <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
            <p className='m-0'>
              Could not load storage locations:{' '}
              {((summary.error ?? suggestion.error) as Error)?.message ?? 'unknown error'}
            </p>
          </div>
        ) : browsing ? (
          <div className='space-y-3'>
            <StorageLocationPicker
              units={unitsWithRoom}
              requires={requires}
              conditions={conditions}
              initialUnitId={picked?.unit.nodeId ?? null}
              selectedSlotId={chosen?.id ?? null}
              onSelectSlot={setChosen}
            />
            <div
              className={cn(
                'rounded-xl border p-3',
                chosen ? 'border-primary/40 bg-primary/5' : 'border-dashed text-sm text-muted-foreground',
              )}
            >
              {chosen ? (
                <>
                  <p className='text-[11px] font-medium uppercase tracking-wider text-muted-foreground'>Selected</p>
                  <LocationTrail path={chosen.path} className='mt-1' />
                </>
              ) : (
                'Pick a building, a room, a storage unit, then a slot.'
              )}
            </div>
          </div>
        ) : suggested ? (
          <div className='rounded-xl border bg-muted/30 p-4'>
            <p className='flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground'>
              <MapPin className='h-3 w-3' /> Designated location
            </p>
            <LocationTrail path={suggested.path} className='mt-2 rounded-lg border bg-background px-3 py-2' />
          </div>
        ) : requires && !picked ? (
          <p className='py-4 text-sm text-muted-foreground'>
            No unit at {profileLabel(requires, conditions)} has room. Free up space in one first.
          </p>
        ) : (
          <p className='py-4 text-sm text-muted-foreground'>
            No free storage position was found. Choose another location to browse, or free up space first.
          </p>
        )}

        {setProductLocation.isError && (
          <p className='text-sm text-destructive'>
            {(setProductLocation.error as Error)?.message ?? 'Could not assign the location.'}
          </p>
        )}

        <DialogFooter>
          {assignedPath ? (
            <Button onClick={handleClose}>Done</Button>
          ) : (
            <>
              {browsing ? (
                <Button
                  variant='outline'
                  onClick={() => {
                    setBrowsing(false)
                    setChosen(null)
                  }}
                >
                  Back to suggestion
                </Button>
              ) : (
                <Button variant='outline' onClick={() => setBrowsing(true)} disabled={blocked}>
                  Choose another location
                </Button>
              )}
              <Button
                onClick={() => target && handleReserve(target)}
                disabled={!target || setProductLocation.isPending}
              >
                {setProductLocation.isPending ? 'Assigning…' : 'Confirm Location'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
